/**
 * Accès IndexedDB minimal : file d'attente hors ligne et cache de lecture.
 */
const DB_NAME = 'adlt';
const DB_VERSION = 1;

function open() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains('queue')) {
        db.createObjectStore('queue', { keyPath: 'id', autoIncrement: true });
      }
      if (!db.objectStoreNames.contains('cache')) {
        db.createObjectStore('cache', { keyPath: 'key' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function tx(store, mode, fn) {
  const db = await open();
  return new Promise((resolve, reject) => {
    const t = db.transaction(store, mode);
    const s = t.objectStore(store);
    const out = fn(s);
    t.oncomplete = () => resolve(out && out.result !== undefined ? out.result : undefined);
    t.onerror = () => reject(t.error);
  });
}

export const idb = {
  queueAdd: (item) => tx('queue', 'readwrite', (s) => s.add(item)),
  queueAll: async () => {
    const db = await open();
    return new Promise((resolve, reject) => {
      const req = db.transaction('queue', 'readonly').objectStore('queue').getAll();
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => reject(req.error);
    });
  },
  queueDelete: (id) => tx('queue', 'readwrite', (s) => s.delete(id)),
  /** Fusionne des champs dans un élément de la file (compteur d'essais). */
  queuePatch: async (id, champs) => {
    const db = await open();
    return new Promise((resolve, reject) => {
      const t = db.transaction('queue', 'readwrite');
      const s = t.objectStore('queue');
      const req = s.get(id);
      req.onsuccess = () => {
        const item = req.result;
        if (item) s.put({ ...item, ...champs });
      };
      t.oncomplete = () => resolve();
      t.onerror = () => reject(t.error);
    });
  },
  /**
   * Miroir des jetons pour le service worker (le localStorage lui est
   * inaccessible). Le store `cache` est purgé à la déconnexion par clearAll().
   */
  tokensSet: (t) => (t
    ? tx('cache', 'readwrite', (s) => s.put({ key: '__tokens', data: t, t: Date.now() }))
    : tx('cache', 'readwrite', (s) => s.delete('__tokens'))),
  tokensGet: async () => {
    const db = await open();
    return new Promise((resolve) => {
      const req = db.transaction('cache', 'readonly').objectStore('cache').get('__tokens');
      req.onsuccess = () => resolve(req.result ? req.result.data : null);
      req.onerror = () => resolve(null);
    });
  },
  cacheSet: (key, data) => tx('cache', 'readwrite', (s) => s.put({ key, data, t: Date.now() })),
  cacheGet: async (key) => {
    const db = await open();
    return new Promise((resolve) => {
      const req = db.transaction('cache', 'readonly').objectStore('cache').get(key);
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => resolve(null);
    });
  },
  clearAll: async () => {
    await tx('queue', 'readwrite', (s) => s.clear());
    await tx('cache', 'readwrite', (s) => s.clear());
  },
};
